// activityHistory.js - Activity History modal content
// Receives activityData, activityLoading, and activityError from
// useModuleMetrics (via moduleMetricsPage.js) instead of a raw module prop.
// Rendered inside the shared Modal component.

import React from "react";
import useTableSort from "../../../hooks/useTableSort";
import SortableTableHeader from "../../modalContent/SortableTableHeader";

// ── Sub-components ────────────────────────────────────────────────────────────

const JobStatusBadge = ({ status }) => {
  const statusClasses = {
    Completed: "mm-job-badge--completed",
    "In Progress": "mm-job-badge--inprogress",
    Pending: "mm-job-badge--pending",
  };

  return (
    <span className={`mm-job-badge ${statusClasses[status] || ""}`}>
      {status}
    </span>
  );
};

/**
 * Formats a worker list for display in the Workers cell.
 *
 * • 0 workers → "-"
 * • 1 worker → employeeName (falls back to employeeId, then "Unknown")
 * • 2+ workers → "First Name +N more"
 */
const WorkerCell = ({ workers }) => {
  if (!workers?.length) return <span className="mm-activity-empty">-</span>;

  const first = workers[0].employeeName || workers[0].employeeId || "Unknown";
  if (workers.length === 1) return <span>{first}</span>;

  return (
    <span
      title={workers
        .map((w) => w.employeeName || w.employeeId || "Unknown")
        .join(", ")}
    >
      {first}{" "}
      <span className="mm-activity-worker-more">
        +{workers.length - 1} more
      </span>
    </span>
  );
};

/**
 * Formats a nullable ISO-8601 date string into a short locale date,
 * optionally appending a raw time string from the backend.
 *
 * Returns "-" when both values are null/undefined.
 */
function formatDateTime(isoDate, rawTime) {
  if (!isoDate && !rawTime) return "-";

  let datePart = "";
  if (isoDate) {
    const d = new Date(isoDate);
    datePart = isNaN(d.getTime())
      ? isoDate
      : d.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
  }

  return rawTime ? `${datePart} ${rawTime}`.trim() : datePart;
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * @param {{
 * moduleInfo: { woNum: string|null, itemNum: string|null, batchNum: string|null } | null,
 * activityData: { jobs: Array, count: number } | null,
 * activityLoading: boolean,
 * activityError: string | null,
 * }} props
 */
const ActivityHistory = ({
  moduleInfo,
  activityData,
  activityLoading,
  activityError,
}) => {
  const { woNum, itemNum, batchNum } = moduleInfo ?? {};

  // ── Table sorting ─────────────────────────────────────────────────────────
  const { sortedData, requestSort, getSortClass } = useTableSort(
    activityData?.jobs || [],
    {
      initialKey: "operationNumber",
      initialDirection: "asc",
    },
  );

  // ── Header description ──────────────────────────────────────────────────────
  const description = woNum ? (
    <p className="modal-description">
      Job history for {woNum}
      {itemNum || batchNum ? (
        <>
          {" "}
          &mdash; {itemNum}
          {batchNum ? <> &bull; {batchNum}</> : null}
        </>
      ) : null}
    </p>
  ) : (
    <p className="modal-description">No module selected</p>
  );

  // ── Body states ─────────────────────────────────────────────────────────────
  let body;

  if (activityLoading) {
    body = (
      <p className="modal-loading-state" aria-live="polite">
        Loading activity history&hellip;
      </p>
    );
  } else if (activityError) {
    body = (
      <p className="modal-error-state" role="alert">
        {activityError}
      </p>
    );
  } else if (!activityData || activityData.jobs.length === 0) {
    body = (
      <p className="modal-empty-state">
        No activity history available for this module.
      </p>
    );
  } else {
    body = (
      <div className="modal-table-scroll">
        <table className="modal-table">
          <thead>
            <tr>
              <SortableTableHeader
                columnKey="operationNumber"
                onSort={requestSort}
                sortClass={getSortClass("operationNumber")}
              >
                Op #
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="jobId"
                onSort={requestSort}
                sortClass={getSortClass("jobId")}
              >
                Job ID
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="jobStatus"
                onSort={requestSort}
                sortClass={getSortClass("jobStatus")}
              >
                Status
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="startDate"
                onSort={requestSort}
                sortClass={getSortClass("startDate")}
              >
                Start
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="endDate"
                onSort={requestSort}
                sortClass={getSortClass("endDate")}
              >
                End
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="hours"
                onSort={requestSort}
                sortClass={getSortClass("hours")}
              >
                Hours
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="workers"
                onSort={requestSort}
                sortClass={getSortClass("workers")}
              >
                Workers
              </SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((job, idx) => (
              <tr key={job.jobId ?? idx}>
                <td>{job.operationNumber ?? "-"}</td>
                <td>{job.jobId ?? "-"}</td>
                <td>
                  {job.jobStatus ? (
                    <JobStatusBadge status={job.jobStatus} />
                  ) : (
                    <span className="mm-activity-empty">-</span>
                  )}
                </td>
                <td>{formatDateTime(job.startDate, job.startTime)}</td>
                <td>{formatDateTime(job.endDate, job.endTime)}</td>
                <td>
                  {job.hours != null ? (
                    `${job.hours.toFixed(1)} h`
                  ) : (
                    <span className="mm-activity-empty">-</span>
                  )}
                </td>
                <td>
                  <WorkerCell workers={job.workers} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {description}

      <div className="modal-section modal-section--scroll">
        <h3>
          Job Activity
          {activityData?.count > 0 && (
            <span className="mm-activity-count"> ({activityData.count})</span>
          )}
        </h3>

        {body}
      </div>
    </>
  );
};

export default ActivityHistory;
